import { z } from 'zod'
import { ACK_TEXT_MAX, ackRecordFields } from './consent.ts'
import { GroupRefSchema, GroupWithSizeSchema, OwnerViewSchema } from './groups.ts'

/**
 * Who something is shared with, and when sharing it needs an administrator (PRD-26).
 *
 * One vocabulary for agents and knowledge bases alike. An audience is *only its owner*, *these
 * groups*, or *everyone*; groups are named by id, never by a string somebody typed, so a group
 * that is renamed or deleted can never leave a share pointing at the wrong people or at nobody
 * without saying so.
 */

export const AudienceKindSchema = z.enum(['private', 'groups', 'everyone'])
export type AudienceKind = z.infer<typeof AudienceKindSchema>

/** What a request carries. `groupIds` is ignored unless `kind` is `groups`, and must be non-empty then. */
export const AudienceInputSchema = z
  .object({
    kind: AudienceKindSchema,
    groupIds: z.array(z.uuid()).max(50).default([])
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.kind === 'groups' && value.groupIds.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['groupIds'], message: 'pick at least one group' })
    }
    if (new Set(value.groupIds).size !== value.groupIds.length) {
      ctx.addIssue({ code: 'custom', path: ['groupIds'], message: 'a group is listed twice' })
    }
  })
export type AudienceInput = z.infer<typeof AudienceInputSchema>

/** The audience an item is live with, as the approval rule reads it. */
export type LiveAudience = { kind: AudienceKind; groupIds: readonly string[] }

export type ApprovalReason = 'everyone' | 'unmanaged_groups'

export type ApprovalVerdict =
  | { required: false; reason: null; unmanagedGroupIds: [] }
  | { required: true; reason: ApprovalReason; unmanagedGroupIds: string[] }

const NOT_REQUIRED: ApprovalVerdict = { required: false, reason: null, unmanagedGroupIds: [] }

/**
 * Whether a change to who can use something needs an administrator's approval (PRD-26 D-13).
 *
 * The product owner's rule, as a pure function: a person who is not an administrator needs
 * approval to widen what is live to **everyone**, or to **any group they do not manage**.
 * Narrowing never does, "only the owner" never does, and groups the actor manages never do.
 *
 * `live` is what is stored on the item now — it moves only through an immediate change or an
 * approval — so an approval "covers the audience" (D-14): once a group is live, publishing a new
 * version to it again widens nothing and needs nothing. `everyone` covers every group, so
 * narrowing from everyone to any set of groups is narrowing.
 *
 * The same function runs in the browser to preview the outcome beside the picker and on the
 * server to decide it; the server's answer is the one that counts.
 */
export const approvalNeeded = (input: {
  actorIsAdmin: boolean
  actorManagedGroupIds: readonly string[]
  live: LiveAudience
  requested: LiveAudience
}): ApprovalVerdict => {
  if (input.actorIsAdmin) return NOT_REQUIRED

  const { live, requested } = input
  if (requested.kind === 'private') return NOT_REQUIRED
  if (live.kind === 'everyone') return NOT_REQUIRED

  if (requested.kind === 'everyone') {
    return { required: true, reason: 'everyone', unmanagedGroupIds: [] }
  }

  const alreadyLive = new Set(live.kind === 'groups' ? live.groupIds : [])
  const managed = new Set(input.actorManagedGroupIds)
  const unmanaged = [...new Set(requested.groupIds)].filter((id) => !alreadyLive.has(id) && !managed.has(id))

  return unmanaged.length === 0
    ? NOT_REQUIRED
    : { required: true, reason: 'unmanaged_groups', unmanagedGroupIds: unmanaged }
}

/**
 * What a change does now, and what waits for review (PRD-26 D-40).
 *
 * The product owner's rule: apply what can be applied now — removals, and groups the actor
 * manages — and send only the rest for review. `now` is the audience to make live immediately, or
 * `null` when nothing changes yet; `review` is what waits (the unmanaged additions, or everyone),
 * or `null` when nothing does. A change from "everyone" to anything is narrowing and never waits.
 */
export const splitChange = (input: {
  actorIsAdmin: boolean
  actorManagedGroupIds: readonly string[]
  live: LiveAudience
  requested: LiveAudience
}): { now: LiveAudience | null; review: { kind: 'groups' | 'everyone'; groupIds: string[] } | null } => {
  const verdict = approvalNeeded(input)
  const same = sameAudience(input.live, input.requested)

  if (!verdict.required) return { now: same ? null : input.requested, review: null }

  if (verdict.reason === 'everyone') {
    // Only a switch to everyone reaches here, and it adds no group of its own: nothing to apply
    // now, and everyone waits.
    return { now: null, review: { kind: 'everyone', groupIds: [] } }
  }

  const waiting = new Set(verdict.unmanagedGroupIds)
  const kept = [...new Set(input.requested.groupIds)].filter((id) => !waiting.has(id))
  const now: LiveAudience = kept.length > 0 ? { kind: 'groups', groupIds: kept } : { kind: 'private', groupIds: [] }
  return {
    now: sameAudience(input.live, now) ? null : now,
    review: { kind: 'groups', groupIds: verdict.unmanagedGroupIds }
  }
}

/**
 * Whether publishing — or bringing a retired agent back — goes live the moment the maker confirms
 * (D-40, D-50). It does when nothing waits; when the audience changes now; when some of the chosen
 * groups do not wait (they may already be the stored audience of an agent that was taken down, so
 * the audience does not change and the agent still has to come back); or when a published agent
 * takes a new version for whoever can already use it (D-35).
 *
 * The server decides with this and the publish dialog words its promise with it: a copy of the
 * rule on each side once let the dialog say "Nothing changes" while the server published at once,
 * and stored that sentence as the maker's acknowledgement.
 */
export const publicationGoesLiveNow = (input: {
  action: 'publish' | 'restore'
  state: string
  requested: LiveAudience
  split: { now: LiveAudience | null; review: { kind: 'groups' | 'everyone'; groupIds: readonly string[] } | null }
}): boolean => {
  const waiting = new Set(input.split.review?.groupIds ?? [])
  const someGoLiveNow = input.split.review?.kind === 'groups' && input.requested.groupIds.some((id) => !waiting.has(id))
  return (
    input.split.review === null ||
    input.split.now !== null ||
    someGoLiveNow ||
    (input.action === 'publish' && input.state === 'published')
  )
}

/** Two audiences reach the same people: same kind, and the same groups when that matters. */
export const sameAudience = (a: LiveAudience, b: LiveAudience): boolean => {
  if (a.kind !== b.kind) return false
  if (a.kind !== 'groups') return true
  const left = new Set(a.groupIds)
  const right = new Set(b.groupIds)
  return left.size === right.size && [...left].every((id) => right.has(id))
}

/**
 * The audience an approval makes live: the requested additions applied onto whatever is live at
 * decision time (D-35), never onto a snapshot the requester saw days ago.
 */
export const applyApproval = (
  liveAtDecision: LiveAudience,
  review: { kind: 'groups' | 'everyone'; groupIds: readonly string[] }
): LiveAudience => {
  if (review.kind === 'everyone' || liveAtDecision.kind === 'everyone') return { kind: 'everyone', groupIds: [] }
  const current = liveAtDecision.kind === 'groups' ? liveAtDecision.groupIds : []
  return { kind: 'groups', groupIds: [...new Set([...current, ...review.groupIds])] }
}

/** The groups a change adds to what is live — the ones whose managers are told (GRP-048). */
export const addedGroupIds = (live: LiveAudience, next: LiveAudience): string[] => {
  // Everyone already reached those groups' members; narrowing to them is not news to anyone.
  if (next.kind !== 'groups' || live.kind === 'everyone') return []
  const before = new Set(live.kind === 'groups' ? live.groupIds : [])
  return [...new Set(next.groupIds)].filter((id) => !before.has(id))
}

// ---------------------------------------------------------------------------
// Who something is shared with, as a payload shows it
// ---------------------------------------------------------------------------

/** An audience with its groups resolved to names and avatars, never bare ids. */
export const AudienceViewSchema = z.object({
  kind: AudienceKindSchema,
  groups: z.array(GroupWithSizeSchema)
})
export type AudienceView = z.infer<typeof AudienceViewSchema>

/** The live audience of an item, in the shape the review rule reads. */
export const liveAudienceOf = (view: AudienceView): LiveAudience => ({
  kind: view.kind,
  groupIds: view.kind === 'groups' ? view.groups.map((group) => group.id) : []
})

/**
 * What a change to who can use something did (PRD-26 D-40).
 *
 * A mixed change is split: the part that needs no review is applied at once (`applied`), and the
 * additions that do become one sharing request (`request`). Either may be absent; both may be
 * present.
 */
export const ShareOutcomeSchema = z.object({
  applied: z.boolean(),
  request: z
    .object({
      id: z.uuid(),
      requestedKind: z.enum(['groups', 'everyone']),
      groups: z.array(GroupRefSchema)
    })
    .nullable()
})
export type ShareOutcome = z.infer<typeof ShareOutcomeSchema>

// ---------------------------------------------------------------------------
// Who can use a connected system or a remote agent (D-25)
// ---------------------------------------------------------------------------

/**
 * Systems have no "only me": they belong to the deployment, and an administrator limits them to
 * everyone or to specific groups. `groups` with no groups left (the last one was deleted, or
 * migration 0013 closed a list of token group names) reaches nobody, and says so — never silently.
 */
export const SystemAccessKindSchema = z.enum(['everyone', 'groups'])
export type SystemAccessKind = z.infer<typeof SystemAccessKindSchema>

export const SystemAccessInputSchema = z
  .object({
    kind: SystemAccessKindSchema,
    groupIds: z.array(z.uuid()).max(50).default([])
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.kind === 'groups' && value.groupIds.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['groupIds'], message: 'pick at least one group' })
    }
    if (new Set(value.groupIds).size !== value.groupIds.length) {
      ctx.addIssue({ code: 'custom', path: ['groupIds'], message: 'a group is listed twice' })
    }
  })
export type SystemAccessInput = z.infer<typeof SystemAccessInputSchema>

export const SystemAccessViewSchema = z.object({
  kind: SystemAccessKindSchema,
  groups: z.array(GroupWithSizeSchema)
})
export type SystemAccessView = z.infer<typeof SystemAccessViewSchema>

// ---------------------------------------------------------------------------
// Sharing requests (D-13, D-29, D-35)
// ---------------------------------------------------------------------------

export const SharingRequestStatusSchema = z.enum([
  'pending',
  'approved',
  'declined',
  'withdrawn',
  'superseded',
  'closed'
])
export type SharingRequestStatus = z.infer<typeof SharingRequestStatusSchema>

/**
 * Why a request ended without a decision. Codes, not sentences: the surface words them.
 *
 * - `group_deleted` — a group it named was deleted.
 * - `requester_lost_authority` — the requester no longer maintains the item.
 * - `item_unavailable` — the item was retired, suspended or deleted.
 * - `migrated` — it named groups from sign-in tokens, which no longer exist (migration 0013).
 */
export const SharingClosedReasonSchema = z.enum([
  'group_deleted',
  'requester_lost_authority',
  'item_unavailable',
  'migrated'
])
export type SharingClosedReason = z.infer<typeof SharingClosedReasonSchema>

export const SharingReasonSchema = z.enum(['everyone', 'unmanaged_groups'])

/** A field that changed between the version asked about and the version that would go live. */
export const SharingDiffEntrySchema = z.object({ field: z.string(), before: z.unknown(), after: z.unknown() })

/** One sharing request, as the administrator's card and the requester's banner read it. */
export const SharingRequestSchema = z.object({
  id: z.uuid(),
  resourceKind: z.enum(['agent', 'knowledge_base']),
  resourceId: z.string(),
  resourceName: z.string(),
  /** The agent's identity tile, for an agent request. */
  resourceIcon: z.string().nullable(),
  resourceAccent: z.string().nullable(),
  requestedBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  requestedAt: z.iso.datetime(),
  status: SharingRequestStatusSchema,
  /** What waits for review: the added groups (with their sizes), or everyone. */
  requested: z.object({
    kind: z.enum(['groups', 'everyone']),
    groups: z.array(GroupWithSizeSchema)
  }),
  /** Who can use it now, at read time. */
  live: AudienceViewSchema,
  reach: z.object({ now: z.number().int().nonnegative(), ifApproved: z.number().int().nonnegative() }),
  reason: SharingReasonSchema,
  /** For an agent: the version asked about, the version live now, and what approval would publish. */
  version: z
    .object({
      requested: z.number().int().positive(),
      live: z.number().int().positive().nullable(),
      wouldPublish: z.number().int().positive()
    })
    .nullable(),
  /** What changed between the version asked about and the one that would publish (empty when the same). */
  changedSince: z.array(SharingDiffEntrySchema),
  /** For an agent: the disclosure, computed for the requested audience. */
  disclosure: z.record(z.string(), z.unknown()),
  /** For a knowledge base: what it holds. */
  base: z
    .object({
      documentCount: z.number().int().nonnegative(),
      connectedFolders: z.number().int().nonnegative(),
      sampleTitles: z.array(z.string())
    })
    .nullable(),
  ackText: z.string(),
  digest: z.string(),
  decidedBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string(),
  closedReason: SharingClosedReasonSchema.nullable()
})
export type SharingRequest = z.infer<typeof SharingRequestSchema>

/**
 * What withdrawing a request did — not the card. A requester who no longer maintains the item may
 * still withdraw what they asked for (it only narrows), but the card shows the item as it is now,
 * which is no longer theirs to read (D-44).
 */
export const SharingRequestWithdrawalSchema = z.object({
  id: z.uuid(),
  status: SharingRequestStatusSchema,
  requestedBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  decidedBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable()
})
export type SharingRequestWithdrawal = z.infer<typeof SharingRequestWithdrawalSchema>

export const SharingRequestListSchema = z.object({ items: z.array(SharingRequestSchema) })
export type SharingRequestList = z.infer<typeof SharingRequestListSchema>

/**
 * An administrator's decision, whole (D-15), bound to the digest they read. A decline carries a
 * note: a refusal nobody explains is how people learn to stop asking rather than to ask better.
 */
export const SharingDecisionSchema = z
  .object({
    decision: z.enum(['approved', 'declined']),
    note: z.string().trim().max(2000).default(''),
    digest: z.string().min(1)
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.decision === 'declined' && value.note === '') {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'say why, so the requester can act on it' })
    }
  })
export type SharingDecision = z.infer<typeof SharingDecisionSchema>

// ---------------------------------------------------------------------------
// Lending: a base answering through one agent (D-16, D-17)
// ---------------------------------------------------------------------------

export const LendingRequestStatusSchema = z.enum(['pending', 'approved', 'declined', 'withdrawn', 'closed'])

export const LendingClosedReasonSchema = z.enum([
  'base_deleted',
  'agent_retired',
  'group_deleted',
  'no_longer_eligible'
])

/** An agent's identity and who maintains it, as a lending decision needs to read it. */
export const LendingAgentSchema = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.string(),
  accent: z.string(),
  owner: OwnerViewSchema,
  state: z.string(),
  /** How many people can use it now — the reach grows with the agent (D-16). */
  reach: z.number().int().nonnegative(),
  audience: AudienceViewSchema,
  /** Whether it can act (send, change, delegate), which the decision states. */
  acts: z.boolean(),
  delegates: z.boolean()
})
export type LendingAgent = z.infer<typeof LendingAgentSchema>

export const LendingRequestSchema = z.object({
  id: z.uuid(),
  baseId: z.uuid(),
  baseName: z.string(),
  agent: z.object({ id: z.string(), name: z.string() }),
  requestedBy: z.object({ id: z.uuid(), displayName: z.string() }),
  origin: z.enum(['maker', 'reader']),
  note: z.string(),
  status: LendingRequestStatusSchema,
  createdAt: z.iso.datetime(),
  decidedBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string(),
  closedReason: LendingClosedReasonSchema.nullable(),
  /** No person maintains the base, so administrators decide (D-17). */
  routedToAdministrators: z.boolean()
})
export type LendingRequest = z.infer<typeof LendingRequestSchema>

/** An agent that has a base attached, as the base's "Agents that use it" list reads it. */
export const AgentUsingBaseSchema = z.object({
  agent: LendingAgentSchema,
  /** Whether the base is attached to it now; a lending on a detached base is inert. */
  attached: z.boolean(),
  lending: z
    .object({
      grantedBy: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
      grantedAt: z.iso.datetime(),
      byAdministrator: z.boolean()
    })
    .nullable(),
  /** People the agent reaches who cannot use the base themselves. */
  beyondAccess: z.number().int().nonnegative(),
  requests: z.array(LendingRequestSchema)
})
export type AgentUsingBase = z.infer<typeof AgentUsingBaseSchema>

export const CreateLendingRequestSchema = z
  .object({
    agentId: z.string().min(1).max(64),
    note: z.string().trim().max(500).default('')
  })
  .strict()

/**
 * Letting an agent answer from a base, with the disclosure the maintainer read (stored verbatim)
 * and, since PRD-27 D-7, the key and values that make it replayable in the other language.
 */
export const LendRequestSchema = z
  .object({
    agentId: z.string().min(1).max(64),
    ackText: z.string().min(1).max(ACK_TEXT_MAX),
    ...ackRecordFields
  })
  .strict()

export const LendingDecisionSchema = z
  .object({
    decision: z.enum(['approved', 'declined']),
    note: z.string().trim().max(2000).default(''),
    /** Required when approving: the disclosure read. */
    ackText: z.string().max(ACK_TEXT_MAX).default(''),
    ...ackRecordFields
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.decision === 'approved' && value.ackText.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['ackText'], message: 'send the sentence that was shown' })
    }
  })
export type LendingDecision = z.infer<typeof LendingDecisionSchema>
